/**
 * Canonical module × action capability matrix for Role & Permission UI.
 * Mirrors backend permission_service MODULE capabilities.
 */

/** @deprecated — inert at runtime; never read, written, or seeded. */
export const DEPRECATED_UNUSED = Object.freeze(['view_profit_pct']);

export const SCOPE_MODULES = Object.freeze(['deals', 'accounts', 'goals', 'commissions']);

export const SCOPE_OPTIONS = [
    { id: 'own', label: 'Own' },
    { id: 'team', label: 'Team' },
    { id: 'all', label: 'All' },
];

/** UI column definitions (null = N/A for that module). */
export const UI_COLUMNS = [
    { id: 'view', label: 'View', action: 'view' },
    { id: 'create', label: 'Create', action: 'create' },
    { id: 'edit', label: 'Edit', action: 'edit' },
    { id: 'delete', label: 'Delete', action: 'delete' },
    { id: 'view_amount', label: 'View Financials', action: 'view_amount' },
    { id: 'export', label: 'Export', action: 'export' },
    { id: 'assign', label: 'Assign', action: 'assign' },
];

export const MODULES = [
    { id: 'deals', label: 'Deals' },
    { id: 'calendar', label: 'Calendar' },
    { id: 'accounts', label: 'Accounts' },
    { id: 'contacts', label: 'Contacts' },
    { id: 'users', label: 'Users' },
    { id: 'integrations', label: 'Integrations' },
    { id: 'commissions', label: 'Commissions' },
    { id: 'goals', label: 'Goals' },
    { id: 'revenue', label: 'Revenue' },
    { id: 'dashboard', label: 'Dashboard' },
    { id: 'reports', label: 'Reports' },
    { id: 'settings', label: 'Settings' },
    { id: 'permissions', label: 'Permissions' },
    { id: 'pipeline', label: 'Pipeline' },
    { id: 'products', label: 'Products' },
];

/** Which UI columns apply per module (true = show checkbox). */
export const MODULE_CAPABILITIES = {
    deals: { view: true, create: true, edit: true, delete: true, view_amount: true, export: true, assign: true },
    calendar: { view: true, create: true, edit: true, delete: false, view_amount: false, export: false, assign: false },
    accounts: { view: true, create: true, edit: true, delete: true, view_amount: true, export: true, assign: true },
    contacts: { view: true, create: true, edit: true, delete: true, view_amount: false, export: true, assign: false },
    users: { view: true, create: true, edit: true, delete: true, view_amount: false, export: true, assign: false },
    integrations: { view: true, create: false, edit: true, delete: false, view_amount: false, export: false, assign: false },
    commissions: { view: true, create: true, edit: true, delete: true, view_amount: true, export: true, assign: false },
    goals: { view: true, create: true, edit: true, delete: true, view_amount: true, export: true, assign: true },
    revenue: { view: true, create: true, edit: true, delete: true, view_amount: true, export: true, assign: false },
    dashboard: { view: true, create: false, edit: false, delete: false, view_amount: true, export: false, assign: false },
    reports: { view: true, create: false, edit: false, delete: false, view_amount: true, export: true, assign: false },
    settings: { view: true, create: false, edit: true, delete: false, view_amount: false, export: false, assign: false },
    permissions: { view: true, create: false, edit: true, delete: false, view_amount: false, export: false, assign: false },
    pipeline: { view: true, create: true, edit: true, delete: true, view_amount: true, export: false, assign: false },
    products: { view: true, create: true, edit: true, delete: true, view_amount: true, export: false, assign: false },
};

/** Users module uses assign_admin / assign_super_admin instead of assign. */
export const USERS_EXTRA_ACTIONS = [
    { id: 'assign_admin', label: 'Assign Admin' },
    { id: 'assign_super_admin', label: 'Assign Super Admin' },
];

/** Commissions-only extras. */
export const COMMISSIONS_EXTRA_ACTIONS = [
    { id: 'approve', label: 'Approve' },
    { id: 'manage', label: 'Manage' },
];

export const DATA_MANAGEMENT_ACTIONS = [
    { id: 'deals', label: 'Deals' },
    { id: 'accounts', label: 'Accounts' },
    { id: 'contacts', label: 'Contacts' },
    { id: 'users', label: 'Users' },
    { id: 'goals', label: 'Goals' },
    { id: 'commissions', label: 'Commissions' },
    { id: 'activities', label: 'Activities' },
    { id: 'purge_all', label: 'Purge all data' },
];

export const DATA_MANAGEMENT_IMPORT_ACTIONS = [
    { id: 'import_deals', label: 'Deals', entity: 'deals' },
    { id: 'import_accounts', label: 'Accounts', entity: 'accounts' },
    { id: 'import_contacts', label: 'Contacts', entity: 'contacts' },
];

/** UI-only auto-suggest when checking permissions (not runtime auth). */
export const PERMISSION_SUGGESTIONS = {
    'deals:view': [
        { module: 'pipeline', action: 'view' },
        { module: 'calendar', action: 'view' },
    ],
    'deals:create': [
        { module: 'pipeline', action: 'view' },
        { module: 'accounts', action: 'view' },
        { module: 'contacts', action: 'view' },
        { module: 'calendar', action: 'create' },
    ],
    'deals:edit': [
        { module: 'pipeline', action: 'view' },
        { module: 'accounts', action: 'view' },
        { module: 'contacts', action: 'view' },
        { module: 'calendar', action: 'edit' },
        { module: 'calendar', action: 'create' },
    ],
};

export function columnEnabled(moduleId, columnId) {
    return MODULE_CAPABILITIES[moduleId]?.[columnId] === true;
}
